import React from "react";

/**
 * TShirtDiagram — SVG with clickable print zones + per-zone pricing engine.
 *
 * Zones (wearer-perspective for chest/sleeve):
 *   left_chest, right_chest, center_chest, left_sleeve, right_sleeve,
 *   back_upper, back_full, neck_label
 *
 * `value` shape (per zone):
 *   { method: "screen"|"embroidery"|"dtf", colors?: number, w?: number, h?: number }
 *   OR truthy scalar (legacy) — treated as { method: default }
 *
 * Pricing rules (per piece):
 *  - Screen: 30 + 15 × (colors - 1) per selected zone. Max A4 area (assumed).
 *  - Embroidery: 60 per zone. Restricted to chest zones (left/right/center) and sleeves.
 *  - DTF: 5 per square inch of the (w × h) print area for that zone.
 *
 * Emits total via `onCost(totalPerPiece, breakdown[])` whenever inputs change.
 */
export const PRINT_ZONES = [
  { key: "left_chest",   label: "Left Chest",   side: "front", embOK: true },
  { key: "right_chest",  label: "Right Chest",  side: "front", embOK: true },
  { key: "center_chest", label: "Center Chest", side: "front", embOK: true },
  { key: "left_sleeve",  label: "Left Sleeve",  side: "front", embOK: true },
  { key: "right_sleeve", label: "Right Sleeve", side: "front", embOK: true },
  { key: "back_upper",   label: "Upper Back",   side: "back",  embOK: false },
  { key: "back_full",    label: "Full Back",    side: "back",  embOK: false },
  { key: "neck_label",   label: "Neck Label",   side: "back",  embOK: false },
];

const ACTIVE = "#C45A41";
const IDLE_STROKE = "#B3AC9E";

// Editable rate table
export const DEFAULT_RATES = {
  screen: { first_color: 30, per_extra_color: 15 },
  embroidery: { flat: 60 },
  dtf: { per_sq_in: 5 },
};

function normalize(v) {
  if (v && typeof v === "object" && !Array.isArray(v)) return v;
  return { method: "screen", colors: 1 };
}

/** Compute per-piece cost for a single zone using its config. */
export function costForZone(zoneKey, cfg, rates = DEFAULT_RATES) {
  const c = normalize(cfg);
  const method = c.method || "screen";
  if (method === "screen") {
    const n = Math.max(1, Number(c.colors) || 1);
    return rates.screen.first_color + (n - 1) * rates.screen.per_extra_color;
  }
  if (method === "embroidery") {
    const zone = PRINT_ZONES.find(z => z.key === zoneKey);
    if (!zone?.embOK) return 0; // embroidery not allowed on back
    return rates.embroidery.flat;
  }
  if (method === "dtf") {
    const w = Math.max(0, Number(c.w) || 0);
    const h = Math.max(0, Number(c.h) || 0);
    return Math.round(w * h * rates.dtf.per_sq_in * 100) / 100;
  }
  return 0;
}

/** Total customization cost across all selected zones. */
export function totalPrintCost(value, rates = DEFAULT_RATES) {
  const entries = Object.entries(value || {});
  const items = entries.map(([k, cfg]) => ({ zone: k, cost: costForZone(k, cfg, rates), cfg: normalize(cfg) }));
  const total = items.reduce((a, b) => a + b.cost, 0);
  return { total: Math.round(total * 100) / 100, items };
}

function ZoneRect({ x, y, w, h, active, onClick, label, testid, rounded = 3 }) {
  return (
    <g style={{ cursor: "pointer" }} onClick={onClick} data-testid={testid}>
      <rect x={x} y={y} width={w} height={h} rx={rounded} ry={rounded}
        fill={active ? "rgba(196,90,65,0.18)" : "rgba(255,255,255,0)"}
        stroke={active ? ACTIVE : IDLE_STROKE}
        strokeWidth={active ? 2 : 1.2}
        strokeDasharray={active ? "0" : "4 3"} />
      <text x={x + w / 2} y={y + h / 2 + 3} fontSize="7.5" textAnchor="middle"
        fill={active ? ACTIVE : "#75706A"} fontFamily="Manrope" fontWeight={active ? 700 : 500}
        style={{ pointerEvents: "none" }}>
        {label}
      </text>
    </g>
  );
}

function ShirtOutline() {
  return (
    <path
      d="M40 30 L80 20 Q100 42 120 20 L160 30 L182 78 L152 90 L152 220 L48 220 L48 90 L18 78 Z"
      fill="#FBF8F1" stroke="#242321" strokeWidth="1.8" strokeLinejoin="round"
    />
  );
}

/** MAIN component */
export default function TShirtDiagram({
  value = {},
  onChange,
  onCost,
  rates = DEFAULT_RATES,
  compact = false,
  readOnly = false,
}) {
  const isActive = (k) => Object.prototype.hasOwnProperty.call(value, k);
  const toggle = (k) => {
    if (readOnly) return;
    const next = { ...value };
    if (isActive(k)) delete next[k];
    else next[k] = { method: "screen", colors: 1 };
    onChange?.(next);
  };
  const setZone = (k, patch) => {
    const cur = normalize(value[k]);
    onChange?.({ ...value, [k]: { ...cur, ...patch } });
  };

  const { total, items: breakdown } = totalPrintCost(value, rates);
  React.useEffect(() => { onCost?.(total, breakdown); /* eslint-disable-next-line */ }, [JSON.stringify(value)]);

  const size = compact ? 140 : 175;
  const activeZones = PRINT_ZONES.filter(z => isActive(z.key));

  return (
    <div className="space-y-3" data-testid="tshirt-diagram">
      <div className="text-[10px] text-[#75706A] italic">
        Tip: “Left” / “Right” are from the wearer's perspective.
      </div>

      <div className="flex gap-4 flex-wrap justify-start items-start">
        {/* FRONT */}
        <div className="text-center">
          <svg width={size} height={size * 1.17} viewBox="0 0 200 240" xmlns="http://www.w3.org/2000/svg">
            <ShirtOutline />
            <ZoneRect x={108} y={70} w={30} h={22} active={isActive("left_chest")}
              onClick={() => toggle("left_chest")} label="L-Chest" testid="zone-left_chest" />
            <ZoneRect x={62} y={70} w={30} h={22} active={isActive("right_chest")}
              onClick={() => toggle("right_chest")} label="R-Chest" testid="zone-right_chest" />
            <ZoneRect x={75} y={100} w={50} h={38} active={isActive("center_chest")}
              onClick={() => toggle("center_chest")} label="Centre" testid="zone-center_chest" />
            <ZoneRect x={155} y={60} w={22} h={20} active={isActive("left_sleeve")}
              onClick={() => toggle("left_sleeve")} label="L-Sleeve" testid="zone-left_sleeve" />
            <ZoneRect x={23} y={60} w={22} h={20} active={isActive("right_sleeve")}
              onClick={() => toggle("right_sleeve")} label="R-Sleeve" testid="zone-right_sleeve" />
            <text x={100} y={235} fontSize="9" fill="#242321" textAnchor="middle"
              fontFamily="Manrope" fontWeight="700" letterSpacing="1">FRONT</text>
          </svg>
        </div>

        {/* BACK */}
        <div className="text-center">
          <svg width={size} height={size * 1.17} viewBox="0 0 200 240" xmlns="http://www.w3.org/2000/svg">
            <ShirtOutline />
            <ZoneRect x={82} y={30} w={36} h={12} active={isActive("neck_label")}
              onClick={() => toggle("neck_label")} label="Neck" testid="zone-neck_label" />
            <ZoneRect x={62} y={55} w={76} h={32} active={isActive("back_upper")}
              onClick={() => toggle("back_upper")} label="Upper Back" testid="zone-back_upper" />
            <ZoneRect x={55} y={95} w={90} h={100} active={isActive("back_full")}
              onClick={() => toggle("back_full")} label="Full Back" testid="zone-back_full" />
            <text x={100} y={235} fontSize="9" fill="#242321" textAnchor="middle"
              fontFamily="Manrope" fontWeight="700" letterSpacing="1">BACK</text>
          </svg>
        </div>

        <div className="text-sm min-w-[220px] flex-1">
          <div className="label-tiny mb-2">Selected areas ({activeZones.length})</div>
          {activeZones.length === 0 && (
            <div className="text-xs text-[#75706A]">Tap a highlighted area on the shirt to add it.</div>
          )}
          <div className="space-y-2">
            {activeZones.map(z => {
              const cfg = normalize(value[z.key]);
              const zCost = costForZone(z.key, cfg, rates);
              const embDisallowed = cfg.method === "embroidery" && !z.embOK;
              return (
                <div key={z.key} className="p-2 rounded-md border border-[#EAE5DB] bg-[#FBF8F1]" data-testid={`zoneconf-${z.key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="badge badge-info">{z.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="mono text-xs font-bold">
                        {embDisallowed ? <span className="text-[#B83A3A]">not allowed</span> : `₹${zCost.toFixed(0)}`}
                      </span>
                      {!readOnly && (
                        <button type="button" className="text-[#B83A3A] text-xs" onClick={() => toggle(z.key)} title="Remove">×</button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <select
                      className="input-field h-7 py-0.5 px-1 text-xs"
                      value={cfg.method || "screen"}
                      disabled={readOnly}
                      onChange={(e) => setZone(z.key, { method: e.target.value })}
                      data-testid={`zone-method-${z.key}`}
                    >
                      <option value="screen">Screen</option>
                      <option value="embroidery" disabled={!z.embOK}>Embroidery{!z.embOK ? " (n/a)" : ""}</option>
                      <option value="dtf">DTF</option>
                    </select>
                    {cfg.method === "screen" && (
                      <input
                        type="number" min="1"
                        className="input-field h-7 py-0.5 px-2 text-xs"
                        placeholder="# colors"
                        value={cfg.colors ?? ""}
                        readOnly={readOnly}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // allow the field to be temporarily empty while user is editing
                          if (raw === "") { setZone(z.key, { colors: "" }); return; }
                          setZone(z.key, { colors: Math.max(1, Number(raw) || 1) });
                        }}
                        onBlur={(e) => {
                          const raw = e.target.value;
                          if (raw === "" || Number(raw) < 1) setZone(z.key, { colors: 1 });
                        }}
                        data-testid={`zone-colors-${z.key}`}
                      />
                    )}
                    {cfg.method === "dtf" && (
                      <div className="flex items-center gap-1 col-span-1">
                        <input
                          type="number" min="0" step="0.5"
                          className="input-field h-7 py-0.5 px-2 text-xs w-full"
                          placeholder="w in"
                          value={cfg.w ?? ""}
                          readOnly={readOnly}
                          onChange={(e) => setZone(z.key, { w: e.target.value === "" ? "" : Number(e.target.value) })}
                          data-testid={`zone-w-${z.key}`}
                        />
                        <span className="text-[10px] text-[#75706A]">×</span>
                        <input
                          type="number" min="0" step="0.5"
                          className="input-field h-7 py-0.5 px-2 text-xs w-full"
                          placeholder="h in"
                          value={cfg.h ?? ""}
                          readOnly={readOnly}
                          onChange={(e) => setZone(z.key, { h: e.target.value === "" ? "" : Number(e.target.value) })}
                          data-testid={`zone-h-${z.key}`}
                        />
                      </div>
                    )}
                    {cfg.method === "embroidery" && (
                      <div className="text-[10px] text-[#75706A] py-1 px-1">Flat ₹{rates.embroidery.flat}/zone · max 3″</div>
                    )}
                  </div>
                  {/* Custom size / free-text note per zone */}
                  <input
                    className="input-field h-7 py-0.5 px-2 text-xs mt-1.5 col-span-2"
                    placeholder='Custom size or note (e.g. "3.5″ round", "matching logo")'
                    value={cfg.custom_size ?? ""}
                    readOnly={readOnly}
                    onChange={(e) => setZone(z.key, { custom_size: e.target.value })}
                    data-testid={`zone-custom-${z.key}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {activeZones.length > 0 && (
        <div className="flex items-center justify-between p-2.5 rounded-md bg-[#DDEADE]" data-testid="tshirt-total">
          <div className="text-xs text-[#2E4A38]">
            <b>Rules</b>: Screen ₹30 first color + ₹15 each extra · Embroidery ₹60/zone (chest &amp; sleeves only) · DTF ₹5/sq in
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#2E4A38]">Cost per piece</div>
            <div className="font-display font-bold text-xl text-[#2E4A38] mono">₹{total.toFixed(2)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
